import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { onAuthStateChanged, signOut, type User } from "firebase/auth";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import type { Timestamp } from "firebase/firestore";
import type { CoachClub } from "@coaching/types";
import { auth, db } from "./firebase";
import { watchCoach } from "./api";

export type CoachClubRow = CoachClub<Timestamp> & { id: string };

export type AuthState =
    | { kind: "loading" }
    | { kind: "signed_out" }
    | { kind: "not_a_coach"; user: User }
    | { kind: "coach"; user: User; clubs: CoachClubRow[] };

const AuthContext = createContext<AuthState>({ kind: "loading" });

/**
 * Who is signed in and whether they coach anywhere. Coach status is the live
 * set of active coachClubs rows, so a revoke takes effect on the next snapshot
 * without a sign-out.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
    const [state, setState] = useState<AuthState>({ kind: "loading" });

    useEffect(() => {
        let stopClubs: (() => void) | null = null;
        const stopAuth = onAuthStateChanged(auth, (user) => {
            stopClubs?.();
            stopClubs = null;
            if (!user) { setState({ kind: "signed_out" }); return; }
            if (user.isAnonymous) { setState({ kind: "not_a_coach", user }); return; }
            setState({ kind: "loading" });
            const q = query(collection(db, "coachClubs"), where("coachId", "==", user.uid), where("status", "==", "active"));
            stopClubs = onSnapshot(q, (snap) => {
                const clubs = snap.docs.map((d) => ({ id: d.id, ...(d.data() as CoachClub<Timestamp>) }));
                setState(clubs.length > 0 ? { kind: "coach", user, clubs } : { kind: "not_a_coach", user });
            }, (err) => {
                console.error("coachClubs subscription failed", err);
                setState({ kind: "not_a_coach", user });
            });
        });
        return () => { stopAuth(); stopClubs?.(); };
    }, []);

    return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
    return useContext(AuthContext);
}

export function useCoach(): { user: User; clubs: CoachClubRow[] } {
    const s = useContext(AuthContext);
    if (s.kind !== "coach") throw new Error("useCoach outside a coach session");
    return { user: s.user, clubs: s.clubs };
}

/**
 * The name the coach shows to players, from their coach profile. The login's own
 * display name can be a club's name when the account is also its admin login.
 */
export function useCoachName(): string {
    const { user } = useCoach();
    const [name, setName] = useState<string | null>(null);
    useEffect(() => watchCoach(user.uid, (c) => setName(c?.displayName ?? null), (e) => {
        console.warn("coach name subscription failed", { uid: user.uid, code: (e as { code?: string }).code });
    }), [user.uid]);
    return name ?? user.displayName ?? user.email ?? "";
}

export const signOutCoach = () => signOut(auth);
